import React, { useState } from "react";
import { AgentService, BlockchainAnalysisResponse, CorrespondenceResponse, Product, WebIntelligenceResponse } from "../../../services/agent.service";
import { ProductMetadata, ProductStatusProps } from "./types";
import { format } from "date-fns";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { User } from "../../../store/user.store";
import { FetchedFile } from "../../../models/uploaded-file";
import { ChevronLeft, ChevronRight, Download, PictureAsPdf, Visibility, WarningSharp } from "@mui/icons-material";

export const ProductStatus = <T extends WebIntelligenceResponse | BlockchainAnalysisResponse | CorrespondenceResponse>({
  productInfo,
  productMetaData,
  onProductUpdate,
}: ProductStatusProps<T>) => {
  const { product } = productInfo;

  const [scheduleTime, setScheduleTime] = useState("");
  const [isUpdatingStop, setIsUpdatingStop] = useState(false);
  const [isUpdatingDelay, setIsUpdatingDelay] = useState(false);
  const [isUpdatingProcess, setIsUpdatingProcess] = useState(false);
  const [showDateInput, setShowDateInput] = useState(false);
  const [showProcessConfirmModal, setShowProcessConfirmModal] = useState(false);
  const [sendMailToUser, setSendMailToUser] = useState(true);

  const handleStopAnalyzeToggle = async (checked: boolean) => {
    setIsUpdatingStop(true);
    try {
      const newForce = checked ? "not-analyze" : "analyze";
      await AgentService.setProductRequestFlags({
        userCrmProfileId: productMetaData.crmProfileId,
        productRequestId: productMetaData.productId,
        productRequestType: productMetaData.productRequestType,
        flags: {
          delayedTo: product.delayedTo,
          force: newForce,
        },
      });

      onProductUpdate({ ...productInfo, product: { ...product, force: newForce } });
    } catch (error) {
      console.error("Error updating flags:", error);
    } finally {
      setIsUpdatingStop(false);
    }
  };

  const handleDelayToggle = (checked: boolean) => {
    if (checked) {
      setShowDateInput(true);
    } else {
      handleClearDelay();
    }
  };

  const handleClearDelay = async () => {
    setIsUpdatingDelay(true);
    try {
      await AgentService.setProductRequestFlags({
        userCrmProfileId: productMetaData.crmProfileId,
        productRequestId: productMetaData.productId,
        productRequestType: productMetaData.productRequestType,
        flags: {
          force: product.force || "analyze",
          delayedTo: null,
        },
      });

      setShowDateInput(false);
      setScheduleTime("");

      onProductUpdate({ ...productInfo, product: { ...product, delayedTo: undefined } });
    } catch (error) {
      console.error("Error clearing schedule:", error);
    } finally {
      setIsUpdatingDelay(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsUpdatingDelay(true);
    try {
      await AgentService.setProductRequestFlags({
        userCrmProfileId: productMetaData.crmProfileId,
        productRequestId: productMetaData.productId,
        productRequestType: productMetaData.productRequestType,
        flags: {
          force: product.force || "analyze",
          delayedTo: scheduleTime,
        },
      });

      setShowDateInput(false);
      setScheduleTime("");

      onProductUpdate({ ...productInfo, product: { ...product, delayedTo: scheduleTime } });
    } catch (error) {
      console.error("Error saving schedule:", error);
    } finally {
      setIsUpdatingDelay(false);
    }
  };

  const handleUpdateSchedule = () => {
    setScheduleTime(product.delayedTo ? format(new Date(product.delayedTo), "yyyy-MM-dd'T'HH:mm") : "");
    setShowDateInput(true);
  };

  const handleProcessImmediatelyClick = () => {
    setShowProcessConfirmModal(true);
  };

  const handleConfirmProcess = async () => {
    setShowProcessConfirmModal(false);
    setIsUpdatingProcess(true);

    try {
      onProductUpdate({ ...productInfo, product: { ...product, isInProcess: true } });

      await AgentService.processProductRequestImmediate({
        userCrmProfileId: productMetaData.crmProfileId,
        productRequestId: productMetaData.productId,
        productRequestType: productMetaData.productRequestType,
        sendMailToUser: sendMailToUser,
      });
    } catch (error) {
      console.error("Error processing immediately:", error);
      // במקרה של שגיאה, החזר את ה-state
      onProductUpdate({ ...productInfo, product: { ...product, isInProcess: false } });
    } finally {
      setIsUpdatingProcess(false);
    }
  };

  const handleCancelProcess = () => {
    setShowProcessConfirmModal(false);
  };

  return (
    <>
      <Grid item xs={12} sm={4}>
        <Section title="Request Status">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
              Is In Process:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={handleProcessImmediatelyClick}
                disabled={product.isInProcess || isUpdatingProcess}
                sx={{
                  px: 2,
                  py: 0.5,
                  fontSize: "12px",
                  textDecoration: "underline",
                  borderRadius: 2,
                  minWidth: "auto",
                }}
              >
                {isUpdatingProcess ? "Processing..." : "Process Now"}
              </Button>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                {product.isInProcess ? "Yes" : "No"}
              </Typography>
            </Box>
          </Box>
          <InfoRow label="Delayed To" value={product.delayedTo ? format(new Date(product.delayedTo), "yyyy-MM-dd HH:mm") : "Not Set"} />
          {/* Force Stop Analyze with Toggle */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
              Forced Not Analyze:
            </Typography>
            <Switch
              checked={product.force === "not-analyze"} // שימוש ישיר ב-product.force
              onChange={(e) => handleStopAnalyzeToggle(e.target.checked)}
              disabled={isUpdatingStop}
              size="small"
              color="warning"
            />
          </Box>
          {/* Delayed To with Toggle and Input */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
              Delayed To:
            </Typography>

            {showDateInput ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  type="datetime-local"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().slice(0, 16) }}
                  size="small"
                  disabled={isUpdatingDelay}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSaveSchedule}
                  disabled={!scheduleTime || isUpdatingDelay}
                  sx={{ minWidth: "50px", px: 1 }}
                >
                  {isUpdatingDelay ? <CircularProgress size={16} /> : "Save"}
                </Button>
              </Box>
            ) : product.delayedTo ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleUpdateSchedule}
                  disabled={isUpdatingDelay}
                  sx={{ minWidth: "40px", px: 0.5, fontSize: "12px", textDecoration: "underline" }}
                >
                  Edit
                </Button>
                <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                  {format(new Date(product.delayedTo), "yyyy-MM-dd HH:mm")}
                </Typography>

                <Switch
                  checked={Boolean(product.delayedTo)}
                  onChange={(e) => handleDelayToggle(e.target.checked)}
                  disabled={isUpdatingDelay}
                  size="small"
                  color="warning"
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch
                  checked={Boolean(product.delayedTo)} // שימוש ישיר ב-product.delayedTo
                  onChange={(e) => handleDelayToggle(e.target.checked)}
                  disabled={isUpdatingDelay}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Section>
      </Grid>

      {/* Process Confirmation Modal */}
      <Dialog
        open={showProcessConfirmModal}
        onClose={handleCancelProcess}
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningSharp color="warning" />
            <Typography variant="h6" component="span">
              Process Immediately
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to process this request immediately? This action will start the processing pipeline and cannot be undone.
          </Typography>

          {!product.hasPaid && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "error.main" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <WarningSharp color="error" fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600, color: "error.main" }}>
                  Payment Warning
                </Typography>
              </Box>
              <Typography variant="body2" color="error.main">
                This product has not been paid for yet. Processing it now may incur costs without guaranteed payment.
              </Typography>
            </Box>
          )}

          {/* Email Settings */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 1, border: 1, borderColor: "primary.light" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: "primary.dark" }}>
              Email Notification Settings
            </Typography>
            <FormControlLabel
              control={<Switch checked={sendMailToUser} onChange={(e) => setSendMailToUser(e.target.checked)} size="small" color="primary" />}
              label={
                <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>
                  Send result file to user via email
                </Typography>
              }
            />
          </Box>

          <Box sx={{ mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Product ID: {productMetaData.productId}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Type: {productMetaData.productRequestType}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Payment Status: {product.hasPaid ? "✅ Paid" : "❌ Not Paid"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Email Notification: {sendMailToUser ? "✅ Enabled" : "❌ Disabled"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 2, gap: 1 }}>
          <Button onClick={handleCancelProcess} variant="outlined" size="small" sx={{ minWidth: 80 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmProcess} variant="contained" color="warning" size="small" sx={{ minWidth: 80 }}>
            Process Now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const InfoRow: React.FC<{ label: string; value: string | number; mono?: boolean }> = ({ label, value, mono = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
      {label}
    </Typography>
    <Tooltip title={value || "N/A"}>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontSize: "0.8rem",
          fontFamily: mono ? "monospace" : "inherit",
          textAlign: "right",
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "default",
        }}
      >
        {value || "N/A"}
      </Typography>
    </Tooltip>
  </Box>
);

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${alpha("#000", 0.08)}`, borderRadius: 1.5, mb: 2 }}>
    <Box sx={{ px: 2, py: 1, bgcolor: alpha("#000", 0.02), borderBottom: `1px solid ${alpha("#000", 0.06)}` }}>
      <Typography variant="subtitle2" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 2, py: 1 }}>{children}</Box>
  </Paper>
);

export const CreatedBy: React.FC<{ createdBy: User | null }> = ({ createdBy }) => {
  if (!createdBy) {
    return (
      <Grid item xs={12} sm={4}>
        <Section title="Created By">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", textAlign: "center", py: 2 }}>
            Information didn't found
          </Typography>
        </Section>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={4}>
      <Section title="Created By">
        <InfoRow label="Full Name" value={createdBy.fullName} />
        <InfoRow label="Email" value={createdBy.email} />
        <InfoRow label="Role" value={createdBy.role || "N/A"} />
        <InfoRow label="User ID" value={createdBy.id} mono />
        <InfoRow label="Is Client" value={createdBy.isClient ? "Yes" : "No"} />
        <InfoRow label="Filled Initial Form" value={createdBy.filledInitialForm ? "Yes" : "No"} />
        <InfoRow
          label="Created At"
          value={createdBy.createdAt && !isNaN(createdBy.createdAt) ? format(new Date(createdBy.createdAt), "yyyy-MM-dd HH:mm") : "N/A"}
        />
      </Section>
    </Grid>
  );
};
export const CrmProductInfo: React.FC<{ productCrm?: Product }> = ({ productCrm }) => {
  if (!productCrm) {
    return (
      <Grid item xs={12} sm={4}>
        <Section title="CRM Product Info">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", textAlign: "center", py: 2 }}>
            CRM information not found
          </Typography>
        </Section>
      </Grid>
    );
  }

  const deals = productCrm.deals || [];
  const totalDealsAmount = deals.reduce((sum, deal) => sum + (deal.amount || 0), 0);
  const leftToPay = (productCrm.totalAmount || 0) - totalDealsAmount;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "done":
        return "success";
      case "pending":
        return "warning";
      case "waiting for actions":
        return "info";
      case "in progress":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <Grid item xs={12} sm={4}>
      <Section title="CRM Product Info">
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            Status
          </Typography>
          <Chip label={productCrm.status} color={getStatusColor(productCrm.status)} size="small" sx={{ fontSize: "0.7rem", height: "20px" }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
            Fully Paid
          </Typography>
          <Chip
            label={leftToPay <= 0 && productCrm.totalAmount ? "✓ Paid" : "Unpaid"}
            color={leftToPay <= 0 && productCrm.totalAmount ? "success" : "error"}
            variant={leftToPay <= 0 && productCrm.totalAmount ? "filled" : "outlined"}
            size="small"
            sx={{ fontSize: "0.7rem", height: "20px" }}
          />
        </Box>
        <InfoRow label="Type" value={productCrm.type} />
        <InfoRow label="Total Amount" value={productCrm.totalAmount ? `$${productCrm.totalAmount}` : "Not Set"} />
        <InfoRow label="Paid Amount" value={`$${totalDealsAmount}`} />
        <InfoRow label="Left to Pay" value={`$${leftToPay}`} />
        <InfoRow label="Contract Signed" value={productCrm.contractSigned ? "Yes" : "No"} />
        <InfoRow label="Created Time" value={productCrm.createdTime ? format(new Date(productCrm.createdTime), "yyyy-MM-dd HH:mm") : "N/A"} />
        {productCrm.requestDetails && Object.keys(productCrm.requestDetails).length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mt: 2, mb: 1 }}>
              Request Details:
            </Typography>
            {Object.entries(productCrm.requestDetails).map(([key, value]) => (
              <InfoRow key={key} label={key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())} value={String(value)} />
            ))}
          </>
        )}
        {deals.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", fontWeight: 600, mt: 2, mb: 1 }}>
              Deals ({deals.length}):
            </Typography>
            {deals.map((deal, index) => (
              <Box key={deal.id} sx={{ mb: 1, pl: 1, borderLeft: `2px solid ${alpha("#000", 0.1)}` }}>
                <InfoRow label={`Deal ${index + 1} Amount`} value={`$${deal.amount || 0}`} />
                <InfoRow label="Deal Created" value={deal.createdTime ? format(new Date(deal.createdTime), "yyyy-MM-dd HH:mm") : "N/A"} />
                {deal.chargedAmount && <InfoRow label="Charged Amount" value={`$${deal.chargedAmount}`} />}
                {deal.invoice && <InfoRow label="Invoice" value="Available" />}
              </Box>
            ))}
          </>
        )}
        {deals.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", mt: 1, fontStyle: "italic" }}>
            No deals found
          </Typography>
        )}
      </Section>
    </Grid>
  );
};

export const AttachedFiles: React.FC<{ attachments: FetchedFile[]; productMetaData: ProductMetadata }> = ({ attachments, productMetaData }) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [loadingStates, setLoadingStates] = useState<Record<string, { preview?: boolean; download?: boolean }>>({});

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -210, // width + gap
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 210, // width + gap
        behavior: "smooth",
      });
    }
  };

  const handleFileAction = async (file: FetchedFile, action: "preview" | "download", clientCrmProfileId: string) => {
    try {
      // Set loading state
      setLoadingStates((prev) => ({
        ...prev,
        [file.id]: {
          ...prev[file.id],
          [action]: true,
        },
      }));

      // Convert FetchedFile to FileWithId format for the service
      const fileForService = {
        id: file.id,
        createdAt: file.Created_Time,
        content: {
          name: file.File_Name,
          size: Number(file.Size),
          type: "pdf", // assuming PDF for now, you might want to determine this from file name
        },
      };

      await AgentService.handleCrmFileAction(fileForService, action, clientCrmProfileId);
    } catch (error) {
      console.error(`Error ${action}ing file:`, error);
    } finally {
      // Clear loading state
      setLoadingStates((prev) => ({
        ...prev,
        [file.id]: {
          ...prev[file.id],
          [action]: false,
        },
      }));
    }
  };

  if (!attachments || attachments.length === 0) {
    return (
      <Section title="Attached Files">
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", textAlign: "center", py: 2 }}>
          No files attached
        </Typography>
      </Section>
    );
  }

  return (
    <Section title={`Attached Files (${attachments.length})`}>
      <Box sx={{ position: "relative" }}>
        {/* Navigation arrows */}
        {attachments.length > 3 && (
          <>
            <IconButton
              onClick={scrollLeft}
              sx={{
                position: "absolute",
                left: -15,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "background.paper",
                boxShadow: 1,
                "&:hover": { bgcolor: "grey.100" },
                width: 32,
                height: 32,
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>

            <IconButton
              onClick={scrollRight}
              sx={{
                position: "absolute",
                right: -15,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "background.paper",
                boxShadow: 1,
                "&:hover": { bgcolor: "grey.100" },
                width: 32,
                height: 32,
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </>
        )}

        {/* Files container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            scrollBehavior: "smooth",
            pb: 1,
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
          }}
        >
          {attachments.map((file) => {
            const fileLoadingState = loadingStates[file.id] || {};
            const isPreviewLoading = fileLoadingState.preview || false;
            const isDownloadLoading = fileLoadingState.download || false;

            return (
              <Box
                key={file.id}
                sx={{
                  minWidth: 200,
                  height: 300,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: "background.paper",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: 2,
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Main content area */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, justifyContent: "center" }}>
                  {/* PDF Icon */}
                  <PictureAsPdf
                    sx={{
                      fontSize: 50,
                      color: "error.main",
                      mb: 2,
                    }}
                  />

                  {/* File name */}
                  <Tooltip title={file.File_Name}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        textAlign: "center",
                        mb: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: 1.2,
                        height: "2.4em",
                      }}
                    >
                      {file.File_Name}
                    </Typography>
                  </Tooltip>

                  {/* File size */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", mb: 1 }}>
                    {file.Size}
                  </Typography>

                  {/* Created time */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", mb: 1 }}>
                    {format(new Date(file.Created_Time), "dd/MM/yy HH:mm")}
                  </Typography>

                  {/* Owner info */}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", mb: 2 }}>
                    By: {file.Owner.name}
                  </Typography>
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isPreviewLoading || isDownloadLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileAction(file, "preview", productMetaData.crmProfileId);
                    }}
                    startIcon={isPreviewLoading ? <CircularProgress size={16} /> : <Visibility />}
                    sx={{
                      flex: 1,
                      fontSize: "0.7rem",
                      py: 0.5,
                      px: 1,
                    }}
                  />

                  <Button
                    variant="contained"
                    size="small"
                    disabled={isPreviewLoading || isDownloadLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileAction(file, "download", productMetaData.crmProfileId);
                    }}
                    startIcon={isDownloadLoading ? <CircularProgress size={16} /> : <Download />}
                    sx={{
                      flex: 1,
                      fontSize: "0.7rem",
                      py: 0.5,
                      px: 1,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Section>
  );
};
